/**
 * Koleksiyon UI yardımcıları — backend rarity/base_score ile uyumlu (combat yok).
 */

export const RARITY_SCORES = {
  common: 10,
  rare: 25,
  epic: 60,
  legendary: 150,
  mythic: 400,
};

export const RARITY_LABELS_TR = {
  common: 'Yaygın',
  uncommon: 'Sıra dışı',
  rare: 'Nadir',
  epic: 'Epik',
  legendary: 'Efsanevi',
  mythic: 'Mitik',
};

/** Tutarlı rarity renkleri: common gri, rare mavi, epic mor, legendary altın, mythic kırmızı-pembe */
export const RARITY_UI = {
  uncommon: {
    border: 'border-slate-500/60',
    text: 'text-slate-300',
    badge: 'bg-slate-600/90 text-slate-100',
    chip: 'border-slate-500 text-slate-300',
  },
  common: {
    border: 'border-gray-500/60',
    text: 'text-gray-400',
    badge: 'bg-gray-600/90 text-gray-100',
    chip: 'border-gray-500 text-gray-400',
  },
  rare: {
    border: 'border-blue-500/70',
    text: 'text-blue-400',
    badge: 'bg-blue-600/90 text-blue-100',
    chip: 'border-blue-500 text-blue-400',
  },
  epic: {
    border: 'border-violet-500/70',
    text: 'text-violet-400',
    badge: 'bg-violet-600/90 text-violet-100',
    chip: 'border-violet-500 text-violet-400',
  },
  legendary: {
    border: 'border-amber-500/80',
    text: 'text-amber-400',
    badge: 'bg-amber-600/90 text-amber-950',
    chip: 'border-amber-500 text-amber-400',
  },
  mythic: {
    border: 'border-pink-500/70',
    text: 'text-pink-300',
    badge: 'bg-gradient-to-r from-red-600/90 to-pink-600/90 text-white',
    chip: 'border-pink-500 text-pink-300 bg-gradient-to-r from-red-500/15 to-pink-500/10',
  },
};

export function rarityKey(r) {
  return RARITY_SCORES[r] !== undefined ? r : 'common';
}

/** Backend `computed_score` (collection_line_score); frontend skor hesaplamaz. */
export function readComputedScore(doc) {
  if (doc == null || typeof doc !== 'object') return 0;
  const n = Number(doc.computed_score);
  if (Number.isFinite(n)) return Math.max(0, Math.floor(n));
  return 0;
}

/** Ödül açılışı / UI’da uncommon ayrı gösterilir; skor tarafında rarityKey hâlâ common’a düşebilir */
export function revealRarityKey(r) {
  const s = String(r || '').toLowerCase();
  if (s === 'uncommon') return 'uncommon';
  return rarityKey(r);
}

/** Karakter / eşya kopya sayısına göre yıldız (backend _stars_from_copy_count ile aynı) */
export function starsFromCopyCount(copyCount) {
  const n = Number(copyCount) || 1;
  if (n < 3) return 1;
  if (n < 6) return 2;
  if (n < 10) return 3;
  return 4;
}

/**
 * Sonraki yıldız eşiği için gereken toplam kopya sayısı (starsFromCopyCount ile uyumlu).
 * 1–2 → 3, 3–5 → 6, 6–9 → 10, 10+ → null (maksimum).
 */
export function getNextStarRequirement(copyCount) {
  const c = Math.max(0, Math.floor(Number(copyCount) || 0));
  if (c >= 10) return null;
  if (c < 3) return 3;
  if (c < 6) return 6;
  return 10;
}

/** UI için: mevcut kopya, sonraki eşik, kalan, maks mu */
export function getStarProgress(copyCount) {
  const c = Math.max(0, Math.floor(Number(copyCount) || 0));
  const effective = Math.max(1, c || 1);
  const nextTotal = getNextStarRequirement(c);
  const isMax = nextTotal === null;
  const remaining = isMax ? 0 : Math.max(0, nextTotal - c);
  return {
    copyCount: c,
    currentStars: starsFromCopyCount(effective),
    nextTotal,
    remaining,
    isMax,
  };
}

/** Koleksiyon kartı / modal tek satır (karakter: yıldız / nüsha) */
export function formatStarProgressLineTR(progress) {
  if (!progress) return '';
  if (progress.isMax) return 'Maksimum destek seviyesine ulaşıldı';
  return `Sonraki yıldız için: ${progress.remaining} nüsha daha gerekli`;
}

/** Karakter detay: nüsha ilerlemesi */
export function formatStarProgressDetailLineTR(progress) {
  if (!progress) return '';
  if (progress.isMax) return `${progress.copyCount} nüsha — maksimum destek seviyesine ulaşıldı`;
  return `${progress.copyCount} nüsha — sonraki yıldız için ${progress.remaining} nüsha daha gerekli`;
}

/** Destek bonusu yüzdesi (taban toplamı üzerinden) */
export function formatStackBonusPercentTR(rate) {
  const p = Math.round(Number(rate) * 100);
  if (!Number.isFinite(p) || p <= 0) return 'Destek Bonusu: %0';
  return `Destek Bonusu: %${p}`;
}

/** İlerleme çubuğu: current / nextTotal oranı (yüzde) */
export function starProgressBarPercent(copyCount) {
  const p = getStarProgress(copyCount);
  if (p.isMax || !p.nextTotal) return { current: p.copyCount, cap: p.copyCount, percent: 100 };
  const cap = p.nextTotal;
  const pct = cap <= 0 ? 0 : Math.min(100, Math.round((p.copyCount / cap) * 100));
  return { current: p.copyCount, cap, percent: pct };
}

/**
 * Aynı katalog satırından çoklu kopya için yıldız / duplikasyon bonusu oranı.
 * Taban: (base × kopya_sayısı) üzerinden uygulanır; ekipman bonusu ile çarpılmaz.
 * Örnek: 3 kopya, base toplam 900 → %20 → +180
 */
export function duplicateStarBonusRate(copyCount) {
  const n = Math.max(0, Math.floor(Number(copyCount) || 0));
  if (n <= 1) return 0;
  if (n === 2) return 0.1;
  if (n < 6) return 0.2;
  if (n < 10) return 0.3;
  return 0.4;
}

export function computeCharacterCollectionTotal(characterDoc) {
  // Item sistemi kaldırıldı — yalnızca karakter taban skoru.
  const base = readComputedScore(characterDoc);
  return { total: base, base };
}

export function parseTs(iso) {
  if (!iso || typeof iso !== 'string') return 0;
  try {
    const fixed = iso.replace('Z', '+00:00');
    return new Date(fixed).getTime();
  } catch {
    return 0;
  }
}

export function collectorTitleFromScore(score) {
  const s = Number(score);
  if (!Number.isFinite(s)) return '—';
  if (s < 200) return 'Başlangıç Koleksiyoncu';
  if (s < 500) return 'Nadir Avcısı';
  if (s < 1200) return 'Elit Koleksiyoncu';
  return 'Mitik Usta';
}

/**
 * Koleksiyon listesi — UI gruplama (instance verisi korunur, sadece görünüm).
 * @param {Array} userCharacters - /user/inventory characters[]
 * @returns {Array<{ base_id: string, ch: object, copies: object[] }>}
 */
export function groupUserCharactersByCatalogId(userCharacters) {
  const map = new Map();
  for (const uc of userCharacters || []) {
    const cid = uc?.character?.character_id;
    if (!cid) continue;
    if (!map.has(cid)) {
      map.set(cid, { base_id: cid, ch: uc.character, copies: [] });
    }
    map.get(cid).copies.push(uc);
  }
  return Array.from(map.values());
}

/**
 * Karakter grubu koleksiyon değeri (UI). Item sistemi kaldırıldığı için
 * sadece taban × kopya + yıldız bonusu üzerinden hesaplanır.
 *
 * @param copies kullanıcının bu karakter için sahip olduğu kopyalar (user_characters[])
 * @param copyCountOverride opsiyonel — progression'dan gelen kopya sayısı
 */
export function computeCharacterGroupCollectionValue(copies, copyCountOverride) {
  const list = copies || [];
  const copyCount =
    copyCountOverride != null && copyCountOverride > 0 ? copyCountOverride : Math.max(1, list.length);
  const ch = list[0]?.character;
  if (!ch?.character_id) {
    return {
      total: 0,
      group_base_total: 0,
      star_bonus_total: 0,
      star_bonus_rate: 0,
      copy_count: copyCount,
      base_per_copy: 0,
    };
  }
  const basePerCopy = readComputedScore(ch);
  const group_base_total = basePerCopy * Math.max(1, copyCount);
  const star_bonus_rate = duplicateStarBonusRate(copyCount);
  const star_bonus_total = Math.floor(group_base_total * star_bonus_rate);
  const total = group_base_total + star_bonus_total;
  return {
    total,
    group_base_total,
    star_bonus_total,
    star_bonus_rate,
    copy_count: copyCount,
    base_per_copy: basePerCopy,
  };
}
